import React from 'react';
import { Link } from 'react-router-dom';
import { BLOG_PAGES } from '../lib/blog-pages';

const CATEGORY_LABELS = {
  profession: 'By Profession',
  city: 'By City',
  sac: 'SAC Codes',
};

const CATEGORY_ORDER = ['profession', 'city', 'sac'];

function pageTitle(p) {
  if (p.title) return p.title;
  if (p.profession) return `GST Invoice Format for ${p.profession} in India`;
  if (p.city) return `Free GST Invoice Generator for ${p.city}`;
  return `SAC ${p.sac} Invoice Format`;
}

function pageDesc(p) {
  return p.intro.length > 120 ? p.intro.slice(0, 117) + '…' : p.intro;
}

export default function Blog() {
  React.useEffect(() => {
    document.title = 'GST Invoice Guides for India — billjaldi';
  }, []);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    pages: BLOG_PAGES.filter((p) => p.category === cat),
  }));

  return (
    <div className="min-h-screen bg-[#F5F5F4]">
      {/* Hero */}
      <div className="bg-slate-900 text-white px-4 py-14 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-orange-400 mb-3">Free Guides</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tight mb-4">
            GST Invoice Guides<br className="hidden sm:block" /> for India
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Everything Indian freelancers, consultants, and small businesses need to know about GST invoicing — with a free generator built right in.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        {grouped.map(({ cat, pages }) => (
          <div key={cat}>
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-heading text-xl font-black text-slate-900">{CATEGORY_LABELS[cat]}</h2>
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">{pages.length} guides</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pages.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-orange-300 transition-all"
                >
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-orange-500 mb-2">
                    {cat === 'profession' ? p.profession : cat === 'city' ? p.city : `SAC ${p.sac}`}
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-sm leading-snug mb-2 group-hover:text-orange-600 transition-colors">
                    {pageTitle(p)}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {pageDesc(p)}
                  </p>
                  <div className="mt-3 text-xs font-bold text-orange-500 group-hover:underline">
                    Read guide →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="bg-slate-900 rounded-2xl p-8 sm:p-10 text-center text-white">
          <h2 className="font-heading text-2xl font-black mb-2">Ready to create your invoice?</h2>
          <p className="text-slate-400 mb-6 text-sm">Free. No signup. Done in 30 seconds.</p>
          <Link
            to="/create"
            className="inline-block bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-3 rounded-lg transition-colors text-sm"
          >
            Create GST Invoice →
          </Link>
        </div>
      </div>
    </div>
  );
}
